import React, { useState, useEffect, useContext } from 'react';
import { collection, doc, onSnapshot, updateDoc, addDoc, serverTimestamp, setDoc, query, where, orderBy } from 'firebase/firestore';
// Fixed useContext import issue
import { db } from '@/lib/firebase';
import { ToastContext } from '@/components/ToastProvider';
import { Store, Settings, Wallet, DollarSign, Save, RefreshCw, Package, Percent } from 'lucide-react';
import MapPicker from '@/components/MapPicker.jsx';
import { getStoreProductsCollection } from '@/lib/storeProducts';
import { STORE_TYPES } from '@/lib/storeManagement';

export default function StoreSettings() {
  const { notify } = useContext(ToastContext);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [stores, setStores] = useState([]);
  const [storeProducts, setStoreProducts] = useState({}); // {storeId: [products]}
  const [expandedStores, setExpandedStores] = useState(new Set());
  const [mapOpen, setMapOpen] = useState({}); // {storeId: boolean}
  const [geocoding, setGeocoding] = useState({}); // {storeId: boolean}
  const [globalSettings, setGlobalSettings] = useState({
    defaultCooperativeFeePercentage: 10,
    minimumCashOutAmount: 100,
    walletEnabled: true
  });

  const mapsKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || (typeof window !== 'undefined' ? (window.PPAC_MAPS_KEY || '') : '');

  // Lazy-load Google Maps JS (no Places lib needed for Geocoder)
  const ensureGoogleLoaded = async () => {
    if (window.google?.maps) return true;
    if (!mapsKey) {
      notify('To use address geocoding or map picker, set VITE_GOOGLE_MAPS_API_KEY (browser key).', 'error');
      return false;
    }
    return await new Promise((resolve) => {
      const existing = document.querySelector('script[data-role="gmaps-loader"]');
      if (existing) {
        existing.addEventListener('load', () => resolve(true), { once: true });
        existing.addEventListener('error', () => resolve(false), { once: true });
        return;
      }
      const s = document.createElement('script');
      s.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(mapsKey)}`;
      s.async = true;
      s.defer = true;
      s.setAttribute('data-role', 'gmaps-loader');
      s.onload = () => resolve(true);
      s.onerror = () => resolve(false);
      document.head.appendChild(s);
    });
  };

  // Load stores and settings
  useEffect(() => {
    const unsubscribeStores = onSnapshot(
      collection(db, 'stores'),
      (snapshot) => {
        const storesData = snapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setStores(storesData);
        setLoading(false);
      },
      (error) => {
        console.error('Error loading stores:', error);
        notify('Error loading stores', 'error');
        setLoading(false);
      }
    );

    // Load global settings
    const unsubscribeSettings = onSnapshot(
      doc(db, 'settings', 'store_wallet'),
      (doc) => {
        if (doc.exists()) {
          setGlobalSettings(doc.data());
        }
      },
      (error) => {
        console.error('Error loading settings:', error);
      }
    );

    return () => {
      unsubscribeStores();
      unsubscribeSettings();
    };
  }, [notify]);

  // Load products for each store when stores change
  useEffect(() => {
    if (stores.length === 0) return;

    const unsubscribes = [];

    stores.forEach(store => {
      try {
        const productsCollection = getStoreProductsCollection(store.type, store.id);
        const productsQuery = query(
          collection(db, productsCollection),
          orderBy('createdAt', 'desc')
        );

        const unsubscribe = onSnapshot(
          productsQuery,
          (snapshot) => {
            const productsData = snapshot.docs.map(doc => ({
              id: doc.id,
              ...doc.data()
            }));
            
            setStoreProducts(prev => ({
              ...prev,
              [store.id]: productsData
            }));
          },
          (error) => {
            console.error(`Error loading products for store ${store.id}:`, error);
            notify(`Error loading products for ${store.name}`, 'error');
          }
        );

        unsubscribes.push(unsubscribe);
      } catch (error) {
        console.error(`Error setting up products listener for store ${store.id}:`, error);
      }
    });

    return () => {
      unsubscribes.forEach(unsubscribe => unsubscribe());
    };
  }, [stores, notify]);

  const handleGlobalSettingsChange = (field, value) => {
    setGlobalSettings(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const saveGlobalSettings = async () => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'settings', 'store_wallet'), {
        ...globalSettings,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      notify('Global settings saved successfully', 'success');
    } catch (error) {
      console.error('Error saving global settings:', error);
      notify('Error saving global settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateStoreSettings = async (storeId, field, value) => {
    try {
      setSaving(true);
      await setDoc(doc(db, 'stores', storeId), {
        [field]: value,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      notify('Store settings updated successfully', 'success');
    } catch (error) {
      console.error('Error updating store settings:', error);
      notify('Error updating store settings', 'error');
    } finally {
      setSaving(false);
    }
  };

  const updateProductFee = async (storeId, productId, feePercentage) => {
    try {
      setSaving(true);
      const fee = feePercentage ? Number(feePercentage) : null;
      
      // Find the store to get its type
      const store = stores.find(s => s.id === storeId);
      if (!store) {
        notify('Store not found', 'error');
        return;
      }
      
      // Get the correct collection name for this store
      const productsCollection = getStoreProductsCollection(store.type, storeId);
      
      await updateDoc(doc(db, productsCollection, productId), {
        cooperativeFeePercentage: fee,
        updatedAt: serverTimestamp()
      });
      notify('Product fee updated successfully', 'success');
    } catch (error) {
      console.error('Error updating product fee:', error);
      notify('Error updating product fee', 'error');
    } finally {
      setSaving(false);
    }
  };

  // Update nested storeLocation object (lat/lng/address) safely
  const updateStoreLocation = async (storeId, currentLocation = {}, patch = {}) => {
    try {
      setSaving(true);
      const next = { ...currentLocation, ...patch };
      await setDoc(doc(db, 'stores', storeId), {
        storeLocation: next,
        lastUpdated: serverTimestamp()
      }, { merge: true });
      notify('Store location updated', 'success');
    } catch (error) {
      console.error('Error updating store location:', error);
      notify('Error updating store location', 'error');
    } finally {
      setSaving(false);
    }
  };

  const toggleMapForStore = async (storeId) => {
    const ok = await ensureGoogleLoaded();
    if (!ok) {
      notify('Google Maps JS not available. Check browser key and API enablement.', 'error');
      return;
    }
    setMapOpen(prev => ({ ...prev, [storeId]: !prev[storeId] }));
  };

  const geocodeStoreAddress = async (store) => {
    const addr = String(store?.storeLocation?.address || '').trim();
    if (!addr) { notify('Enter a store address first.', 'error'); return; }
    setGeocoding(prev => ({ ...prev, [store.id]: true }));
    try {
      const ok = await ensureGoogleLoaded();
      if (!ok || !window.google?.maps) {
        notify('Google Maps JS not available. Check browser key and API enablement.', 'error');
        return;
      }
      const geocoder = new window.google.maps.Geocoder();
      geocoder.geocode({ address: addr }, async (results, status) => {
        if (status === 'OK' && results && results[0]) {
          const loc = results[0].geometry.location;
          const lat = typeof loc.lat === 'function' ? loc.lat() : loc.lat;
          const lng = typeof loc.lng === 'function' ? loc.lng() : loc.lng;
          const formatted = results[0].formatted_address || addr;
          await updateStoreLocation(store.id, store.storeLocation || {}, { lat: Number(lat), lng: Number(lng), address: formatted });
        } else {
          notify(`Geocoding failed: ${status}`, 'error');
        }
      });
    } catch (e) {
      notify('Geocoding error. Please try again.', 'error');
    } finally {
      setGeocoding(prev => ({ ...prev, [store.id]: false }));
    }
  };

  const toggleStoreExpansion = (storeId) => {
    setExpandedStores(prev => {
      const newSet = new Set(prev);
      if (newSet.has(storeId)) {
        newSet.delete(storeId);
      } else {
        newSet.add(storeId);
      }
      return newSet;
    });
  };

  const getStoreProducts = (storeId) => {
    // Get products for the specific store
    return storeProducts[storeId] || [];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-64">
        <RefreshCw className="w-8 h-8 animate-spin text-blue-600" />
        <span className="ml-2 text-gray-600">Loading store settings...</span>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6">
      {/* Header - Mobile Optimized */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-gray-900 flex items-center">
            <Settings className="w-6 h-6 sm:w-8 sm:h-8 mr-2 sm:mr-3 text-blue-600" />
            Store Settings
          </h1>
          <p className="text-sm sm:text-base text-gray-600 mt-1">Manage store wallet settings and cooperative fees</p>
        </div>
      </div>

      {/* Global Settings - Mobile Optimized */}
      <div className="bg-white rounded-lg shadow p-4 sm:p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4 flex items-center">
          <DollarSign className="w-5 h-5 mr-2 text-green-600" />
          Global Settings
        </h2>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Default Cooperative Fee Percentage
            </label>
            <div className="relative">
              <input
                type="number"
                min="0"
                max="50"
                value={globalSettings.defaultCooperativeFeePercentage}
                onChange={(e) => handleGlobalSettingsChange('defaultCooperativeFeePercentage', Number(e.target.value))}
                className="input pr-8"
              />
              <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
            </div>
            <p className="text-xs text-gray-500 mt-1">Percentage of order total that goes to cooperative</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Minimum Cash Out Amount
            </label>
            <div className="relative">
              <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500">₱</span>
              <input
                type="number"
                min="0"
                value={globalSettings.minimumCashOutAmount}
                onChange={(e) => handleGlobalSettingsChange('minimumCashOutAmount', Number(e.target.value))}
                className="input pl-8"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">Minimum amount required for cash out requests</p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Wallet System Status
            </label>
            <select
              value={globalSettings.walletEnabled ? 'enabled' : 'disabled'}
              onChange={(e) => handleGlobalSettingsChange('walletEnabled', e.target.value === 'enabled')}
              className="input"
            >
              <option value="enabled">Enabled</option>
              <option value="disabled">Disabled</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Enable or disable store wallet system</p>
          </div>
        </div>

        <div className="mt-6 flex justify-end">
          <button
            onClick={saveGlobalSettings}
            disabled={saving}
            className="btn btn-primary flex items-center w-full sm:w-auto justify-center"
          >
            {saving ? (
              <>
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                <span className="hidden sm:inline">Saving...</span>
                <span className="sm:hidden">Saving...</span>
              </>
            ) : (
              <>
                <Save className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Save Global Settings</span>
                <span className="sm:hidden">Save Settings</span>
              </>
            )}
          </button>
        </div>
      </div>

      {/* Store-Specific Settings - Mobile Optimized */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-4 sm:px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900 flex items-center">
            <Store className="w-5 h-5 mr-2 text-blue-600" />
            Store-Specific Settings
          </h2>
          <p className="text-sm text-gray-600 mt-1">Individual store wallet and fee settings</p>
        </div>

        <div className="p-4 sm:p-6">
          {stores.length === 0 ? (
            <div className="text-center py-8 text-gray-500">
              No stores found. Create stores first to manage their settings.
            </div>
          ) : (
            <div className="space-y-6">
              {stores.map((store) => {
                const storeProducts = getStoreProducts(store.id);
                const isExpanded = expandedStores.has(store.id);
                
                return (
                  <div key={store.id} className="border border-gray-200 rounded-lg p-4">
                    {/* Mobile: Stacked layout */}
                    <div className="block sm:hidden mb-4">
                      <div className="mb-3">
                        <h3 className="text-lg font-medium text-gray-900">{store.name}</h3>
                        <p className="text-sm text-gray-600">
                          {store.type === 'cooperative' ? 'Cooperative Store' : 
                           store.type === 'member' ? 'Member Store' : 'Establishment Store'}
                        </p>
                      </div>
                      <div className="space-y-2">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center space-x-2">
                            <Wallet className="w-5 h-5 text-green-600" />
                            <span className="text-sm font-medium text-gray-900">
                              ₱{(store.walletBalance || 0).toLocaleString()}
                            </span>
                          </div>
                        </div>
                        <button
                          onClick={() => toggleStoreExpansion(store.id)}
                          className="btn btn-sm btn-outline flex items-center w-full justify-center"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          {isExpanded ? 'Hide' : 'Show'} Products ({storeProducts.length})
                        </button>
                      </div>
                    </div>
                    
                    {/* Desktop: Original layout */}
                    <div className="hidden sm:flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-lg font-medium text-gray-900">{store.name}</h3>
                        <p className="text-sm text-gray-600">
                          {store.type === 'cooperative' ? 'Cooperative Store' : 
                           store.type === 'member' ? 'Member Store' : 'Establishment Store'}
                        </p>
                      </div>
                      <div className="flex items-center space-x-4">
                        <div className="flex items-center space-x-2">
                          <Wallet className="w-5 h-5 text-green-600" />
                          <span className="text-sm font-medium text-gray-900">
                            ₱{(store.walletBalance || 0).toLocaleString()}
                          </span>
                        </div>
                        <button
                          onClick={() => toggleStoreExpansion(store.id)}
                          className="btn btn-sm btn-outline flex items-center"
                        >
                          <Package className="w-4 h-4 mr-2" />
                          {isExpanded ? 'Hide' : 'Show'} Products ({storeProducts.length})
                        </button>
                      </div>
                    </div>

                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Default Cooperative Fee Percentage
                        </label>
                        <div className="relative">
                          <input
                            type="number"
                            min="0"
                            max="50"
                            value={store.cooperativeFeePercentage || globalSettings.defaultCooperativeFeePercentage}
                            onChange={(e) => updateStoreSettings(store.id, 'cooperativeFeePercentage', Number(e.target.value))}
                            className="input pr-8"
                          />
                          <span className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500">%</span>
                        </div>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Wallet Status
                        </label>
                        <select
                          value={store.walletEnabled ? 'enabled' : 'disabled'}
                          onChange={(e) => updateStoreSettings(store.id, 'walletEnabled', e.target.value === 'enabled')}
                          className="input"
                        >
                          <option value="enabled">Enabled</option>
                          <option value="disabled">Disabled</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                          Last Updated
                        </label>
                        <div className="text-sm text-gray-600 py-2">
                          {store.lastUpdated ? 
                            new Date(store.lastUpdated.toDate()).toLocaleString() : 
                            'Never'
                          }
                        </div>
                      </div>
                    </div>

                    {/* Products Section */}
                  {/* Store Location Editor */}
                  <div className="mt-4 border-t border-gray-200 pt-4">
                    <h4 className="text-md font-medium text-gray-900 mb-2">Store Location</h4>
                    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 mb-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Latitude</label>
                        <input
                          type="number"
                          step="0.000001"
                          value={store.storeLocation?.lat ?? ''}
                          onChange={(e) => updateStoreLocation(store.id, store.storeLocation || {}, { lat: Number(e.target.value) })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Longitude</label>
                        <input
                          type="number"
                          step="0.000001"
                          value={store.storeLocation?.lng ?? ''}
                          onChange={(e) => updateStoreLocation(store.id, store.storeLocation || {}, { lng: Number(e.target.value) })}
                          className="input w-full"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">Address</label>
                        <input
                          type="text"
                          value={store.storeLocation?.address || ''}
                          onChange={(e) => updateStoreLocation(store.id, store.storeLocation || {}, { address: e.target.value })}
                          className="input w-full"
                          placeholder="Store address"
                        />
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2 mb-2">
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => geocodeStoreAddress(store)}
                        disabled={!!geocoding[store.id]}
                      >
                        {geocoding[store.id] ? 'Finding coordinates…' : 'Use address → coordinates'}
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-outline"
                        onClick={() => toggleMapForStore(store.id)}
                      >
                        {mapOpen[store.id] ? 'Hide map picker' : 'Pick on map'}
                      </button>
                    </div>
                    {mapOpen[store.id] && (
                      <div className="mt-2 border rounded p-2">
                        {mapsKey ? (
                          <MapPicker
                            lat={store.storeLocation?.lat}
                            lng={store.storeLocation?.lng}
                            apiKey={mapsKey}
                            onChange={async (la, ln) => {
                              await updateStoreLocation(store.id, store.storeLocation || {}, { lat: la, lng: ln });
                            }}
                          />
                        ) : (
                          <div className="rounded border bg-gray-50 p-2 text-xs text-ink/60">
                            To enable the map picker, set VITE_GOOGLE_MAPS_API_KEY in your environment and enable Maps JavaScript API.
                          </div>
                        )}
                        <div className="text-xs text-ink/60 mt-2">
                          Current: {Number(store.storeLocation?.lat || 0).toFixed(6)}, {Number(store.storeLocation?.lng || 0).toFixed(6)}
                        </div>
                      </div>
                    )}
                  </div>

                  {/* Products Section */}
                    {isExpanded && (
                      <div className="border-t border-gray-200 pt-4">
                        <div className="flex items-center mb-4">
                          <Package className="w-5 h-5 mr-2 text-blue-600" />
                          <h4 className="text-md font-medium text-gray-900">Product Fee Settings</h4>
                          <span className="ml-2 text-sm text-gray-500">({storeProducts.length} products)</span>
                        </div>
                        
                        {storeProducts.length === 0 ? (
                          <div className="text-center py-4 text-gray-500">
                            No products available for this store.
                          </div>
                        ) : (
                          <div className="space-y-2 max-h-96 overflow-y-auto">
                            {storeProducts.map((product) => (
                              <div key={product.id} className="p-3 bg-gray-50 rounded-lg">
                                {/* Mobile: Stacked layout */}
                                <div className="block sm:hidden">
                                  <div className="mb-2">
                                    <div className="font-medium text-gray-900 truncate">{product.name}</div>
                                    <div className="text-sm text-gray-600">
                                      SKU: {product.sku || 'N/A'} • Price: ₱{Number(product.price || 0).toLocaleString()}
                                    </div>
                                  </div>
                                  <div className="flex items-center justify-between">
                                    <div className="flex items-center space-x-2">
                                      <Percent className="w-4 h-4 text-gray-400" />
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={product.cooperativeFeePercentage || ''}
                                        onChange={(e) => updateProductFee(store.id, product.id, e.target.value)}
                                        placeholder="Store default"
                                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                      <span className="text-sm text-gray-500">%</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {product.cooperativeFeePercentage ? 
                                        `${product.cooperativeFeePercentage}%` : 
                                        'Store default'
                                      }
                                    </div>
                                  </div>
                                </div>
                                
                                {/* Desktop: Original layout */}
                                <div className="hidden sm:flex items-center justify-between">
                                  <div className="flex-1 min-w-0">
                                    <div className="font-medium text-gray-900 truncate">{product.name}</div>
                                    <div className="text-sm text-gray-600">
                                      SKU: {product.sku || 'N/A'} • Price: ₱{Number(product.price || 0).toLocaleString()}
                                    </div>
                                  </div>
                                  <div className="flex items-center space-x-3 ml-4">
                                    <div className="flex items-center space-x-2">
                                      <Percent className="w-4 h-4 text-gray-400" />
                                      <input
                                        type="number"
                                        min="0"
                                        max="100"
                                        step="0.1"
                                        value={product.cooperativeFeePercentage || ''}
                                        onChange={(e) => updateProductFee(store.id, product.id, e.target.value)}
                                        placeholder="Store default"
                                        className="w-20 px-2 py-1 text-sm border border-gray-300 rounded focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                                      />
                                      <span className="text-sm text-gray-500">%</span>
                                    </div>
                                    <div className="text-xs text-gray-500">
                                      {product.cooperativeFeePercentage ? 
                                        `${product.cooperativeFeePercentage}%` : 
                                        'Store default'
                                      }
                                    </div>
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
